import {
  Box,
  Checkbox,
  Flex,
  Image,
  Input,
  Portal,
  Spinner,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useState } from "react";
import PriceAdjustedByTypeSelect from "@/features/order-sheet-sets/price-adjusted-by-type-select";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { LineSheetSetProduct } from "@/features/line-sheet-sets/hooks/use-list-line-sheet-set-products";
import { PriceAdjustedBy } from "@/features/line-sheet-sets/line-sheet-set.types";

const PREVIEW_SIZE = 240;
const PREVIEW_OFFSET = 16;

type ConditionBy = PriceAdjustedBy["by"];

export interface ProductConditionRow {
  productId: number;
  product: LineSheetSetProduct;
  by: ConditionBy;
  value: string;
}

export type ValueSortDir = "none" | "asc" | "desc";

interface ProductConditionTableProps {
  rows: ProductConditionRow[];
  filteredIndexes: number[];
  selectedIds: Set<number>;
  isLoading: boolean;
  isAllFilteredSelected: boolean;
  isSomeFilteredSelected: boolean;
  hasFiltered: boolean;
  maxH?: string;
  valueSortDir?: ValueSortDir;
  onToggleValueSort?: () => void;
  onToggleSelectAll: () => void;
  onToggleRow: (productId: number) => void;
  onChangeRow: (index: number, patch: Partial<ProductConditionRow>) => void;
}

export default function ProductConditionTable({
  rows,
  filteredIndexes,
  selectedIds,
  isLoading,
  isAllFilteredSelected,
  isSomeFilteredSelected,
  hasFiltered,
  maxH = "480px",
  valueSortDir = "none",
  onToggleValueSort,
  onToggleSelectAll,
  onToggleRow,
  onChangeRow,
}: ProductConditionTableProps) {
  const { tTitle } = useI18nHelper();
  // 이미지 컬럼: 기본 1개(작게), 헤더 토글로 최대 3개까지 펼치기(크게)
  const [imagesExpanded, setImagesExpanded] = useState(false);
  const imageColWidth = imagesExpanded ? "188px" : "72px";
  const imageSize = imagesExpanded ? "56px" : "36px";
  const [preview, setPreview] = useState<{
    src: string;
    x: number;
    y: number;
  } | null>(null);

  const handlePreviewEnter = (src: string, e: React.MouseEvent) => {
    setPreview({ src, x: e.clientX, y: e.clientY });
  };
  const handlePreviewMove = (e: React.MouseEvent) => {
    setPreview((prev) =>
      prev ? { ...prev, x: e.clientX, y: e.clientY } : prev
    );
  };
  const handlePreviewLeave = () => setPreview(null);

  const previewLeft =
    preview &&
    (preview.x + PREVIEW_OFFSET + PREVIEW_SIZE >
    (typeof window !== "undefined" ? window.innerWidth : Infinity)
      ? preview.x - PREVIEW_OFFSET - PREVIEW_SIZE
      : preview.x + PREVIEW_OFFSET);
  const previewTop =
    preview &&
    (preview.y + PREVIEW_OFFSET + PREVIEW_SIZE >
    (typeof window !== "undefined" ? window.innerHeight : Infinity)
      ? preview.y - PREVIEW_OFFSET - PREVIEW_SIZE
      : preview.y + PREVIEW_OFFSET);

  return (
    <Box
      border={"0.5px solid #D4D4D1"}
      borderRadius={"6px"}
      overflow={"hidden"}
      background={"#FFFFFF"}
    >
      <Flex
        alignItems={"center"}
        gap={"8px"}
        padding={"8px 14px"}
        fontSize={"12px"}
        fontWeight={"600"}
        color={"#6F6F6C"}
        background={"#F7F9FA"}
        borderBottom={"0.5px solid #E8E8E5"}
      >
        <Box width={"24px"} flexShrink={0}>
          <Checkbox
            isChecked={isAllFilteredSelected}
            isIndeterminate={isSomeFilteredSelected}
            onChange={onToggleSelectAll}
            isDisabled={!hasFiltered}
          />
        </Box>
        <Flex width={imageColWidth} flexShrink={0} alignItems={"center"} gap={"4px"}>
          <Text>{tTitle("image")}</Text>
          <Box
            as={"button"}
            type={"button"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            width={"16px"}
            height={"16px"}
            flexShrink={0}
            borderRadius={"3px"}
            border={"0.5px solid #D4D4D1"}
            background={"#FFFFFF"}
            color={"#8F8F8C"}
            lineHeight={1}
            fontSize={"11px"}
            _hover={{ background: "#EFEFED", color: "#444444" }}
            onClick={() => setImagesExpanded((v) => !v)}
            title={imagesExpanded ? tTitle("collapse") : tTitle("expand")}
          >
            {imagesExpanded ? "«" : "»"}
          </Box>
        </Flex>
        <Box flex={1} minW={0}>
          {tTitle("brand")} / {tTitle("sku")} / {tTitle("etc_info")}
        </Box>
        <Box width={"200px"}>
          {tTitle("gender")} / {tTitle("category")} / {tTitle("season")}
        </Box>
        <Box width={"120px"}>{tTitle("condition")}</Box>
        <Flex
          width={"80px"}
          flexShrink={0}
          alignItems={"center"}
          justifyContent={"flex-end"}
          gap={"2px"}
          cursor={onToggleValueSort ? "pointer" : "default"}
          userSelect={"none"}
          onClick={onToggleValueSort}
          _hover={onToggleValueSort ? { color: "#444444" } : undefined}
          title={tTitle("sort_by_value")}
        >
          <Text>{tTitle("value")}</Text>
          <Text
            fontSize={"10px"}
            lineHeight={1}
            color={valueSortDir === "none" ? "#C4C4C1" : "#48B5FE"}
          >
            {valueSortDir === "asc" ? "▲" : valueSortDir === "desc" ? "▼" : "⇅"}
          </Text>
        </Flex>
      </Flex>

      {isLoading ? (
        <Flex justifyContent={"center"} padding={"24px"}>
          <Spinner size={"sm"} />
        </Flex>
      ) : filteredIndexes.length === 0 ? (
        <Box padding={"16px 14px"}>
          <Text fontSize={"12px"} color={"#8F8F8C"}>
            {tTitle("no_data")}
          </Text>
        </Box>
      ) : (
        <Box maxH={maxH} overflowY={"auto"}>
          {filteredIndexes.map((idx, viewIdx) => {
            const row = rows[idx];
            const isSelected = selectedIds.has(row.productId);
            return (
              <Flex
                key={row.productId}
                alignItems={"center"}
                gap={"8px"}
                padding={"8px 14px"}
                borderTop={viewIdx === 0 ? "none" : "0.5px solid #F0F0ED"}
                background={isSelected ? "#F0F9FF" : undefined}
                cursor={"pointer"}
                onClick={() => onToggleRow(row.productId)}
                _hover={{
                  background: isSelected ? "#E6F4FF" : "#FAFAFA",
                }}
              >
                <Box
                  width={"24px"}
                  flexShrink={0}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    isChecked={isSelected}
                    onChange={() => onToggleRow(row.productId)}
                  />
                </Box>
                <Flex
                  width={imageColWidth}
                  flexShrink={0}
                  gap={"4px"}
                  onClick={(e) => e.stopPropagation()}
                >
                  {(row.product.images ?? [])
                    .slice(0, imagesExpanded ? 3 : 1)
                    .map((src, imgIdx) => (
                      <Box
                        key={`${row.productId}-${imgIdx}`}
                        width={imageSize}
                        height={imageSize}
                        flexShrink={0}
                        borderRadius={"4px"}
                        background={"#FCFCFE"}
                        overflow={"hidden"}
                        border={"0.5px solid #E8E8E5"}
                        onMouseEnter={(e) => handlePreviewEnter(src, e)}
                        onMouseMove={handlePreviewMove}
                        onMouseLeave={handlePreviewLeave}
                      >
                        <Image
                          src={src}
                          alt={row.product.sku}
                          objectFit={"contain"}
                          width={"100%"}
                          height={"100%"}
                        />
                      </Box>
                    ))}
                </Flex>
                <Flex
                  flexDirection={"column"}
                  gap={"2px"}
                  flex={1}
                  minW={0}
                >
                  <Text
                    fontSize={"12px"}
                    fontWeight={"600"}
                    color={"#1A1A1A"}
                    overflow={"hidden"}
                    textOverflow={"ellipsis"}
                    whiteSpace={"nowrap"}
                  >
                    {row.product.brandName}
                  </Text>
                  <Text
                    fontSize={"11px"}
                    color={"#6F6F6C"}
                    overflow={"hidden"}
                    textOverflow={"ellipsis"}
                    whiteSpace={"nowrap"}
                  >
                    {row.product.sku}
                  </Text>
                  {(() => {
                    const parts = [row.product.productName];
                    if (row.product.note) {
                      parts.push(...Object.values(row.product.note));
                    }
                    const text = parts.filter(Boolean).join(" ") || "-";
                    // 양이 많아 ellipsis로 잘릴 때 hover로 전체 내용 확인.
                    // "-"(빈 데이터)는 툴팁 안 띄움.
                    return (
                      <Tooltip
                        label={text}
                        isDisabled={text === "-"}
                        placement={"top-start"}
                        openDelay={300}
                        hasArrow
                        bg={"#1A1A1A"}
                        color={"#FFFFFF"}
                        fontSize={"11px"}
                        padding={"6px 8px"}
                        borderRadius={"4px"}
                        maxW={"480px"}
                      >
                        <Text
                          fontSize={"11px"}
                          color={"#6F6F6C"}
                          overflow={"hidden"}
                          textOverflow={"ellipsis"}
                          whiteSpace={"nowrap"}
                        >
                          {text}
                        </Text>
                      </Tooltip>
                    );
                  })()}
                </Flex>
                <Flex
                  flexDirection={"column"}
                  gap={"2px"}
                  width={"200px"}
                  flexShrink={0}
                  fontSize={"11px"}
                  color={"#6F6F6C"}
                >
                  <Text
                    overflow={"hidden"}
                    textOverflow={"ellipsis"}
                    whiteSpace={"nowrap"}
                  >
                    {row.product.gender || "-"}
                  </Text>
                  {/* 카테고리 > 서브카테고리 한 줄로 표시. 둘 중 하나만 있으면 그것만.
                       양이 많아 ellipsis로 잘릴 때 hover로 전체 내용 확인. */}
                  {(() => {
                    const text =
                      row.product.category && row.product.subCategory
                        ? `${row.product.category} > ${row.product.subCategory}`
                        : row.product.category || row.product.subCategory || "-";
                    return (
                      <Tooltip
                        label={text}
                        isDisabled={text === "-"}
                        placement={"top-start"}
                        openDelay={300}
                        hasArrow
                        bg={"#1A1A1A"}
                        color={"#FFFFFF"}
                        fontSize={"11px"}
                        padding={"6px 8px"}
                        borderRadius={"4px"}
                        maxW={"480px"}
                      >
                        <Text
                          overflow={"hidden"}
                          textOverflow={"ellipsis"}
                          whiteSpace={"nowrap"}
                        >
                          {text}
                        </Text>
                      </Tooltip>
                    );
                  })()}
                  <Text
                    overflow={"hidden"}
                    textOverflow={"ellipsis"}
                    whiteSpace={"nowrap"}
                  >
                    {row.product.season || "-"}
                  </Text>
                </Flex>
                <Box
                  width={"120px"}
                  flexShrink={0}
                  onClick={(e) => e.stopPropagation()}
                >
                  <PriceAdjustedByTypeSelect
                    width={"120px"}
                    value={row.by}
                    onSelect={(v) => {
                      if (v) onChangeRow(idx, { by: v });
                    }}
                  />
                </Box>
                <Input
                  width={"80px"}
                  flexShrink={0}
                  type={"number"}
                  fontSize={"12px"}
                  height={"28px"}
                  padding={"0 8px"}
                  textAlign={"right"}
                  borderColor={"#D4D4D1"}
                  background={"#FFFFFF"}
                  _hover={{ borderColor: "#D4D4D1" }}
                  _focus={{
                    borderColor: "#8F8F8C",
                    boxShadow: "none",
                  }}
                  value={row.value}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    onChangeRow(idx, { value: e.target.value })
                  }
                />
              </Flex>
            );
          })}
        </Box>
      )}
      {preview && (
        <Portal>
          <Box
            position={"fixed"}
            left={`${previewLeft}px`}
            top={`${previewTop}px`}
            width={`${PREVIEW_SIZE}px`}
            height={`${PREVIEW_SIZE}px`}
            borderRadius={"6px"}
            overflow={"hidden"}
            background={"#FFFFFF"}
            border={"0.5px solid #D4D4D1"}
            boxShadow={"0 4px 12px rgba(0, 0, 0, 0.18)"}
            pointerEvents={"none"}
            zIndex={2000}
          >
            <Image
              src={preview.src}
              alt={""}
              objectFit={"contain"}
              width={"100%"}
              height={"100%"}
            />
          </Box>
        </Portal>
      )}
    </Box>
  );
}
